/**
 * CurrencyRatesCard — Apr 29, 2026 (Yash).
 *
 * Settings panel that lets admins maintain INR-anchored exchange rates
 * for the non-India currencies the CRM accepts.
 *
 * Formula (per the team):
 *   target_price = USD_price × (USD-INR ÷ TARGET-INR)
 * Rounded standard half-up (9.2→9, 9.5→10, 9.51→10).
 *
 * UI:
 *   1. Editable grid of currency rows (3 cols on desktop). Each row =
 *      currency code + INR rate input + remove button.
 *   2. "Add currency" inline button.
 *   3. Live "Test Conversion" panel — agent types a USD price, sees the
 *      rounded value in every active currency. Recomputes locally so it
 *      reflects unsaved edits.
 *   4. "Save rates" button — admin / super_admin only. Surfaces the
 *      backend's validation errors as toasts.
 */
import { useState, useEffect, useMemo } from "react";
import { Loader2, Save, Plus, X, RefreshCw, Calculator, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { settingsApi } from "../lib/api";

const ROUND_HALF_UP = (n) => Math.floor(Number(n) + 0.5);

const convertUsdTo = (target, usd, rates) => {
  if (!rates || target === "USD") return ROUND_HALF_UP(usd);
  if (target === "INR") {
    const r = Number(rates.USD || 0);
    return r ? ROUND_HALF_UP(Number(usd) * r) : null;
  }
  const u = Number(rates.USD || 0);
  const t = Number(rates[target] || 0);
  if (!u || !t) return null;
  return ROUND_HALF_UP(Number(usd) * (u / t));
};

const CurrencyRatesCard = ({ user }) => {
  const [rates, setRates] = useState({});
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testUsd, setTestUsd] = useState(1000);
  const [testItemcode, setTestItemcode] = useState("");
  const [newCur, setNewCur] = useState("");
  const [newRate, setNewRate] = useState("");

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getCurrencyRates();
      setRates(res.data?.rates || {});
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by });
    } catch (e) {
      toast.error("Failed to load currency rates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateRate = (cur, value) =>
    setRates((r) => ({ ...r, [cur]: value === "" ? "" : Number(value) }));
  const removeCur = (cur) => {
    if (cur === "USD") {
      toast.error("USD-INR is the anchor — it can't be removed");
      return;
    }
    setRates((r) => {
      const next = { ...r };
      delete next[cur];
      return next;
    });
  };
  const addCur = () => {
    const code = (newCur || "").trim().toUpperCase();
    if (!/^[A-Z]{3,4}$/.test(code)) { toast.error("Enter a valid 3-letter ISO code"); return; }
    if (rates[code] != null) { toast.error(`${code} already exists`); return; }
    const r = Number(newRate);
    if (!r || r <= 0) { toast.error("Rate must be > 0"); return; }
    setRates((s) => ({ ...s, [code]: r }));
    setNewCur(""); setNewRate("");
  };

  const save = async () => {
    setSaving(true);
    try {
      // Drop any blank inputs before sending
      const cleaned = {};
      for (const [k, v] of Object.entries(rates)) {
        if (v !== "" && Number(v) > 0) cleaned[k.toUpperCase()] = Number(v);
      }
      const res = await settingsApi.updateCurrencyRates(cleaned);
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by });
      toast.success("Currency rates saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rates");
    } finally {
      setSaving(false);
    }
  };

  // Compute the live preview locally so unsaved edits are reflected instantly.
  const conversions = useMemo(() => {
    const out = [];
    const sortedKeys = Object.keys(rates).sort();
    for (const cur of sortedKeys) {
      const v = convertUsdTo(cur, testUsd, rates);
      out.push({ cur, value: v });
    }
    out.unshift({ cur: "INR", value: convertUsdTo("INR", testUsd, rates) });
    return out;
  }, [rates, testUsd]);

  const sortedKeys = useMemo(() => Object.keys(rates).sort(), [rates]);

  if (!isAdmin) return null;

  return (
    <div className="bento-card lg:col-span-2" data-testid="currency-rates-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
            <RefreshCw size={18} />
          </div>
          <div>
            <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>
              Currency Exchange Rates
            </h2>
            <p className="text-[11px] text-[--text-muted] leading-tight mt-0.5">
              INR-anchored rates. Used to convert USD product prices into any non-India currency at the moment of payment.
            </p>
            {meta.updated_at && (
              <p className="text-[10px] text-[--text-muted] mt-0.5">
                Last updated {new Date(meta.updated_at).toLocaleString()} {meta.updated_by ? `by ${meta.updated_by}` : ""}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-[12px] py-2 px-4 rounded-xl flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-40"
          data-testid="currency-rates-save-btn"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {saving ? "Saving…" : "Save rates"}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4 text-[11px] text-amber-900 flex items-start gap-2">
        <AlertCircle size={13} className="mt-0.5 shrink-0" />
        <span>
          Each rate is "1&nbsp;UNIT&nbsp;=&nbsp;X&nbsp;INR" (e.g. 1&nbsp;GBP&nbsp;=&nbsp;95&nbsp;INR). 
          Conversions use <span className="font-mono font-bold">USD&nbsp;×&nbsp;(USD-INR&nbsp;÷&nbsp;TARGET-INR)</span>, 
          rounded half-up. <span className="font-bold">USD-INR is the anchor and required.</span>
        </span>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[--text-muted] text-[12px]">
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Loading rates…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4" data-testid="currency-rates-grid">
            {sortedKeys.map((cur) => (
              <div key={cur} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-emerald-300 transition-all" data-testid={`rate-row-${cur}`}>
                <span className="font-mono text-[12px] font-extrabold text-slate-700 w-12 shrink-0">{cur}</span>
                <span className="text-[10px] text-slate-400 mr-1">=</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates[cur] ?? ""}
                  onChange={(e) => updateRate(cur, e.target.value)}
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[12px] outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 font-mono"
                  data-testid={`rate-input-${cur}`}
                />
                <span className="text-[10px] font-bold text-slate-400 mr-1">INR</span>
                <button
                  onClick={() => removeCur(cur)}
                  disabled={cur === "USD"}
                  className="text-slate-300 hover:text-rose-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  title={cur === "USD" ? "USD is the anchor — can't be removed" : `Remove ${cur}`}
                  data-testid={`rate-remove-${cur}`}
                >
                  <X size={13} />
                </button>
              </div>
            ))}

            {/* Add new currency */}
            <div className="flex items-center gap-2 bg-emerald-50 border border-dashed border-emerald-300 rounded-xl px-3 py-2">
              <input
                type="text"
                value={newCur}
                onChange={(e) => setNewCur(e.target.value.toUpperCase().slice(0, 4))}
                placeholder="MXN"
                maxLength={4}
                className="w-12 bg-white border border-emerald-200 rounded-lg px-1.5 py-1 text-[12px] outline-none font-mono font-bold text-center"
                data-testid="new-currency-code"
              />
              <span className="text-[10px] text-slate-400">=</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="rate in INR"
                className="flex-1 bg-white border border-emerald-200 rounded-lg px-2 py-1 text-[12px] outline-none font-mono"
                data-testid="new-currency-rate"
              />
              <button
                onClick={addCur}
                className="text-emerald-600 hover:text-emerald-800 font-bold flex items-center gap-1 text-[11px]"
                data-testid="add-currency-btn"
              >
                <Plus size={13} /> Add
              </button>
            </div>
          </div>

          {/* Live test calculator */}
          <div className="bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 border border-violet-200 rounded-2xl p-4" data-testid="currency-test-calculator">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
                <Calculator size={14} />
              </div>
              <div>
                <p className="text-[12px] font-extrabold text-violet-900 leading-tight">Test Conversion</p>
                <p className="text-[10px] text-violet-700 leading-tight">Type a USD price to see it rounded into every currency. Reflects unsaved edits.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600 block mb-0.5">Itemcode (optional reference)</span>
                <input
                  value={testItemcode}
                  onChange={(e) => setTestItemcode(e.target.value.toUpperCase())}
                  className="w-full bg-white border border-violet-200 rounded-lg px-2.5 py-1.5 text-[12px] outline-none focus:ring-1 focus:ring-violet-200 font-mono"
                  placeholder="e.g. ZJ40"
                  data-testid="test-itemcode-input"
                />
              </label>
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600 block mb-0.5">USD price *</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={testUsd}
                  onChange={(e) => setTestUsd(e.target.value)}
                  className="w-full bg-white border border-violet-200 rounded-lg px-2.5 py-1.5 text-[12px] outline-none focus:ring-1 focus:ring-violet-200 font-mono font-bold"
                  data-testid="test-usd-input"
                />
              </label>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-violet-100">
              <p className="text-[9px] font-bold uppercase tracking-wider text-violet-500 mb-2">
                ${Number(testUsd || 0).toLocaleString()} USD {testItemcode ? `(${testItemcode})` : ""} converts to:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                {conversions.map(({ cur, value }) => (
                  <div
                    key={cur}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[11px] font-mono ${
                      cur === "INR" ? "bg-amber-100 border border-amber-200" : "bg-slate-50 border border-slate-100"
                    }`}
                    data-testid={`test-result-${cur}`}
                  >
                    <span className={`font-extrabold ${cur === "INR" ? "text-amber-800" : "text-slate-600"}`}>{cur}</span>
                    <span className={`font-bold ${cur === "INR" ? "text-amber-900" : "text-slate-800"}`}>
                      {value == null ? "—" : Number(value).toLocaleString("en-IN")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CurrencyRatesCard;
